import { useEffect, useRef, useState } from "react"
import Link from "next/link"

import { Button } from "@coval/ui"

import { createNotebook } from "../utils/notebooksApi"
import type { Notebook } from "../utils/notebookSnapshot"

export function CreateBlankNotebook({
  organizationSlug,
  workspaceId,
  onCreated,
  recipeHref,
}: {
  organizationSlug: string
  workspaceId: string
  onCreated: (id: string) => void
  recipeHref: string
}) {
  const [error, setError] = useState("")
  const [attempt, setAttempt] = useState(0)
  const delivered = useRef(false)
  const request = useRef<Promise<Notebook> | null>(null)
  useEffect(() => {
    let active = true
    request.current ??= createNotebook({
      organizationSlug,
      workspaceId,
      mode: "blank",
      title: "Untitled notebook",
      snapshot: { version: 1, cells: [] },
      messages: [],
    })
    void request.current
      .then((notebook) => {
        if (active && !delivered.current) {
          delivered.current = true
          onCreated(notebook.id)
        }
      })
      .catch((failure: unknown) => {
        if (active) {
          setError(
            failure instanceof Error
              ? failure.message
              : "Could not create a notebook.",
          )
        }
      })
    return () => {
      active = false
    }
  }, [attempt, onCreated, organizationSlug, workspaceId])
  return (
    <div className="space-y-4 p-8">
      {error ? (
        <>
          <p role="alert" className="text-coval-danger-text">
            {error}
          </p>
          <Button
            onClick={() => {
              request.current = null
              setError("")
              setAttempt((value) => value + 1)
            }}
          >
            Try again
          </Button>
        </>
      ) : (
        <p role="status">Opening your blank notebook…</p>
      )}
      {error && (
        <Link
          href={recipeHref}
          className="text-sm text-coval-info-text underline"
        >
          Create from a recipe or schedule instead
        </Link>
      )}
    </div>
  )
}
